// Merkan · Anliegen an parlinex (KI-07)
// Öffnet sich über supportOeffnen() aus dem Menü („Support“), der Hilfe oder Hugo; eingebunden am Hilfe-Symbol.
// Am Rechner mittig, am Handy als Blatt von unten. Eingaben bleiben beim Schließen erhalten, bis die Anfrage gesendet ist.
// Strg oder Cmd und Enter sendet. Nach dem Senden wechselt der Inhalt zur Bestätigung mit Anfragenummer.
// Die Anfrage landet im Service Hub von parlinex (Migration 0164); die Antwort kommt per Mail oder,
// wenn parlinex im Admin Center antwortet, in der Glocke und unter Einstellungen › Support-Anfragen.
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as TastenEreignis } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Check, Copy, HelpCircle, LifeBuoy, Lightbulb } from 'lucide-react';
import { IcSchliessen as X } from '../../ui/icons';
import { Hinweis, Knopf, SpeichernKnopf } from '../../ui';
import { useMandantId, useSession } from '../../lib/session';
import { t } from '../../lib/sprache';
import { alsBetreff, geraetText, seitenName, supportAnfrageSenden, supportGesendetMelden, type SupportArt } from '../../lib/support';
import './support.css';

export interface SupportVorlage {
  quelle: 'hilfe' | 'hugo';
  /** Kurze Frage, z. B. aus der Hilfesuche oder die letzte Frage an Hugo; wird zum Betreff */
  frage?: string;
  betreff?: string;
  text?: string;
  art?: SupportArt;
  verlauf?: { rolle: string; inhalt: string }[];
  /** Bezug, an den das Ergebnis zurückgemeldet wird (z. B. die Hugo-Antwort, unter der weitergegeben wurde) */
  bezug?: string;
  kontext?: Record<string, unknown>;
}

/** Anliegen an parlinex öffnen (Menü, Hilfe, Hugo); das Fenster hängt am Hilfe-Symbol, das immer eingebunden ist */
export const supportOeffnen = (v: SupportVorlage) => window.dispatchEvent(new CustomEvent<SupportVorlage>('merkan:support', { detail: v }));

interface Entwurf { art: SupportArt; betreff: string; text: string; dringend: boolean; kontextMit: boolean }
const LEER: Entwurf = { art: 'frage', betreff: '', text: '', dringend: false, kontextMit: true };
const SCHLUESSEL = 'merkan.support.entwurf';

function entwurfLesen(): Entwurf {
  try { const r = localStorage.getItem(SCHLUESSEL); if (r) return { ...LEER, ...(JSON.parse(r) as Partial<Entwurf>) }; } catch { /* egal */ }
  return LEER;
}
function entwurfSchreiben(e: Entwurf | null) {
  try { if (e && (e.betreff.trim() || e.text.trim())) localStorage.setItem(SCHLUESSEL, JSON.stringify(e)); else localStorage.removeItem(SCHLUESSEL); } catch { /* egal */ }
}

const ARTEN: { wert: SupportArt; icon: typeof HelpCircle; titel: string; text: string; betreff: string; beschreibung: string }[] = [
  { wert: 'frage', icon: HelpCircle, titel: t('Frage zur Bedienung'), text: t('Du weißt nicht, wie etwas geht'),
    betreff: t('Zum Beispiel: Sammelpostfach verbinden'), beschreibung: t('Was möchtest du erreichen, und an welcher Stelle kommst du nicht weiter?') },
  { wert: 'fehler', icon: Bug, titel: t('Etwas klappt nicht'), text: t('Fehlermeldung oder unerwartetes Verhalten'),
    betreff: t('Zum Beispiel: Rechnung wird nicht festgeschrieben'), beschreibung: t('Was hast du gemacht, was ist passiert, und was hättest du erwartet? Eine Fehlermeldung gern wörtlich.') },
  { wert: 'wunsch', icon: Lightbulb, titel: t('Idee oder Wunsch'), text: t('Was dir die Arbeit leichter machen würde'),
    betreff: t('Zum Beispiel: Vorgänge nach Hafen filtern'), beschreibung: t('Was würde dir helfen, und in welcher Situation brauchst du das?') },
];

const istMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

function startEntwurf(v: SupportVorlage): Entwurf {
  if (v.betreff || v.text || v.frage) {
    const frage = (v.frage ?? '').trim();
    const kurz = frage.length <= 120 && !frage.includes('\n');
    return {
      ...LEER, art: v.art ?? 'frage',
      betreff: v.betreff ?? (frage ? alsBetreff(frage) : ''),
      text: v.text ?? (frage && !kurz ? frage : ''),
    };
  }
  return entwurfLesen();
}

export default function SupportDialog({ vorlage, schliessen }: { vorlage: SupportVorlage; schliessen: () => void }) {
  const mandantId = useMandantId();
  const { session } = useSession();
  const email = session?.user.email ?? '';
  const ort = useLocation();
  const id = useId();
  const vorbelegt = Boolean(vorlage.betreff || vorlage.text || vorlage.frage);
  const mitVerlauf = Boolean(vorlage.verlauf?.length);

  const [e, setE] = useState<Entwurf>(() => startEntwurf(vorlage));
  const setzen = (p: Partial<Entwurf>) => setE((alt) => ({ ...alt, ...p }));
  // Nur freie Eingaben als Entwurf merken, keine Vorbelegung aus Hugo oder der Hilfe
  useEffect(() => { if (!vorbelegt) entwurfSchreiben(e); }, [e, vorbelegt]);

  const [verlaufMit, setVerlaufMit] = useState(mitVerlauf);
  const [zustand, setZustand] = useState<'bereit' | 'speichert' | 'gespeichert'>('bereit');
  const [fehler, setFehler] = useState<string | null>(null);
  const [pruefen, setPruefen] = useState(false);
  const [ergebnis, setErgebnis] = useState<{ nummer: string | null; dringend: boolean } | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const betreffRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fertigRef = useRef<HTMLButtonElement>(null);

  const seite = seitenName(ort.pathname);
  const geraet = geraetText();
  const art = ARTEN.find((a) => a.wert === e.art) ?? ARTEN[0];
  const betreffFehlt = e.betreff.trim().length < 3;

  const schliessenSicher = useCallback(() => { if (zustand !== 'speichert') schliessen(); }, [zustand, schliessen]);
  useEffect(() => {
    const h = (ev: KeyboardEvent) => { if (ev.key === 'Escape') schliessenSicher(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [schliessenSicher]);

  // Fokus: leerer Betreff zuerst, sonst die Beschreibung, nach dem Senden „Fertig“
  useEffect(() => {
    if (ergebnis) { fertigRef.current?.focus(); return; }
    (e.betreff.trim() ? textRef.current : betreffRef.current)?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ergebnis]);

  async function senden() {
    if (zustand === 'speichert') return;
    setPruefen(true);
    setFehler(null);
    if (betreffFehlt) { betreffRef.current?.focus(); return; }
    setZustand('speichert');
    const kontext: Record<string, unknown> = { ...(e.kontextMit ? { seite, geraet } : {}), ...(vorlage.kontext ?? {}) };
    if (verlaufMit && vorlage.verlauf?.length) kontext.verlauf = vorlage.verlauf.slice(-8).map((v) => ({ rolle: v.rolle, inhalt: v.inhalt.slice(0, 2000) }));
    const dringend = e.art === 'fehler' && e.dringend;
    const r = await supportAnfrageSenden(mandantId, { art: e.art, betreff: e.betreff, text: e.text, dringend, quelle: vorlage.quelle, kontext });
    if (!r.ok) { setZustand('bereit'); setFehler(r.hinweis ?? t('Die Anfrage konnte nicht gesendet werden.')); return; }
    setZustand('gespeichert');
    entwurfSchreiben(null);
    if (r.nummer) supportGesendetMelden(vorlage.bezug, r.nummer);
    setErgebnis({ nummer: r.nummer ?? null, dringend });
  }

  const taste = (ev: TastenEreignis) => {
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); void senden(); }
  };

  async function nummerKopieren() {
    if (!ergebnis?.nummer) return;
    try { await navigator.clipboard.writeText(ergebnis.nummer); setKopiert(true); window.setTimeout(() => setKopiert(false), 1600); } catch { /* egal */ }
  }

  return (
    <>
      <div className="schleier su-schleier" onClick={schliessenSicher} />
      <div className="su-blatt" role="dialog" aria-modal="true" aria-labelledby={`${id}-titel`} onKeyDown={taste} data-tour="support-dialog">
        <span className="su-griff" aria-hidden="true" />
        <header className="su-kopf">
          {!ergebnis && <span className="su-marke" aria-hidden="true"><LifeBuoy size={18} /></span>}
          <div className="su-kopf-text">
            <h2 id={`${id}-titel`} className={ergebnis ? 'su-unsichtbar' : undefined}>{ergebnis ? t('Anfrage gesendet') : t('Anliegen an parlinex')}</h2>
            {!ergebnis && <p>{t('Beschreib kurz, worum es geht. Wir melden uns bei dir.')}</p>}
          </div>
          <button type="button" className="su-x" onClick={schliessenSicher} aria-label={t('Schließen')} title={t('Schließen (Esc)')}><X size={18} /></button>
        </header>

        {ergebnis ? (
          <div className="su-inhalt su-bestaetigung" key="fertig" aria-live="polite">
            <svg className="su-haken" viewBox="0 0 52 52" aria-hidden="true">
              <circle className="su-haken-kreis" cx="26" cy="26" r="24" />
              <path className="su-haken-strich" d="M15 27.5l7 7 15-16" />
            </svg>
            <h3>{t('Ist bei parlinex angekommen')}</h3>
            {ergebnis.nummer && (
              <p>
                {t('Deine Anfrage hat die Nummer')}{' '}
                <button type="button" className="su-nummer" onClick={() => void nummerKopieren()} title={t('Nummer kopieren')}>
                  <span className="mono">{ergebnis.nummer}</span>{kopiert ? <Check size={13} /> : <Copy size={13} />}
                </button>
              </p>
            )}
            <p className="su-leise">
              {email ? t('Die Antwort kommt per Mail an {email} oder in die Glocke.', { email }) : t('Die Antwort kommt per Mail oder in die Glocke.')}
              {ergebnis.dringend ? ` ${t('Du hast sie als dringend markiert, wir sehen sie uns zuerst an.')}` : ''}
            </p>
            <span className="su-kopiert" aria-live="polite">{kopiert ? t('Nummer kopiert') : ''}</span>
          </div>
        ) : (
          <div className="su-inhalt" key="formular">
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

            <fieldset className="su-arten">
              <legend>{t('Worum geht es?')}</legend>
              <div className="su-arten-raster" role="radiogroup">
                {ARTEN.map((a) => {
                  const Icon = a.icon;
                  const aktiv = a.wert === e.art;
                  return (
                    <button
                      key={a.wert} type="button" role="radio" aria-checked={aktiv} className="su-art" data-art={a.wert} tabIndex={aktiv ? 0 : -1}
                      onClick={() => setzen({ art: a.wert })}
                      onKeyDown={(ev) => {
                        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(ev.key)) return;
                        ev.preventDefault();
                        const i = ARTEN.findIndex((x) => x.wert === e.art);
                        const n = ARTEN[(i + (ev.key === 'ArrowRight' || ev.key === 'ArrowDown' ? 1 : ARTEN.length - 1)) % ARTEN.length];
                        setzen({ art: n.wert });
                        (ev.currentTarget.parentElement?.querySelector(`[data-art="${n.wert}"]`) as HTMLButtonElement | null)?.focus();
                      }}
                    >
                      <span className="su-art-icon"><Icon size={17} /></span>
                      <span className="su-art-titel">{a.titel}</span>
                      <span className="su-art-text">{a.text}</span>
                      <span className="su-art-punkt" aria-hidden="true"><Check size={11} strokeWidth={3} /></span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <label className="su-feld">
              <span>{t('Betreff')}</span>
              <input
                ref={betreffRef} value={e.betreff} maxLength={160} onChange={(ev) => setzen({ betreff: ev.target.value })} data-tour="support-betreff"
                placeholder={art.betreff} aria-invalid={pruefen && betreffFehlt} aria-describedby={pruefen && betreffFehlt ? `${id}-b` : undefined}
              />
              {pruefen && betreffFehlt && <small className="su-fehler" id={`${id}-b`}>{t('Ein paar Worte genügen, damit wir die Anfrage zuordnen können.')}</small>}
            </label>

            <label className="su-feld">
              <span>{t('Beschreibung')} <em className="su-optional">{t('optional')}</em></span>
              <textarea ref={textRef} value={e.text} rows={5} maxLength={8000} onChange={(ev) => setzen({ text: ev.target.value })} placeholder={art.beschreibung} data-tour="support-frage" />
            </label>

            {e.art === 'fehler' && (
              <label className="su-schalter">
                <input type="checkbox" role="switch" checked={e.dringend} onChange={(ev) => setzen({ dringend: ev.target.checked })} />
                <span className="su-schalter-bahn" aria-hidden="true"><span /></span>
                <span className="su-schalter-text">
                  <strong>{t('Ich kann gerade nicht weiterarbeiten')}</strong>
                  <small>{t('Wir sehen dringende Anfragen zuerst an.')}</small>
                </span>
              </label>
            )}

            <div className="su-mit">
              {mitVerlauf && (
                <label className="su-mit-zeile su-mit-wahl">
                  <input type="checkbox" checked={verlaufMit} onChange={(ev) => setVerlaufMit(ev.target.checked)} />
                  <span>{t('Gespräch mit {ki} mitsenden')}<small>{t('Die letzten Nachrichten, damit wir nicht noch einmal nachfragen müssen')}</small></span>
                </label>
              )}
              <label className="su-mit-zeile su-mit-wahl">
                <input type="checkbox" checked={e.kontextMit} onChange={(ev) => setzen({ kontextMit: ev.target.checked })} />
                <span>{t('Seite und Gerät mitsenden')}<small>{seite} · {geraet}</small></span>
              </label>
              <p className="su-mit-zeile su-hinweis">{t('Bitte keine Passwörter oder Bankdaten eintragen.')}</p>
            </div>
          </div>
        )}

        <footer className="su-fuss">
          {ergebnis ? (
            <button ref={fertigRef} type="button" className="kn kn-primaer su-fertig" onClick={schliessen}>{t('Fertig')}</button>
          ) : (
            <>
              <span className="su-fuss-info">{email ? t('Antwort an {email}', { email }) : ''}</span>
              <Knopf onClick={schliessenSicher} disabled={zustand === 'speichert'}>{t('Abbrechen')}</Knopf>
              <SpeichernKnopf
                zustand={zustand} onClick={() => void senden()}
                texte={{ bereit: t('Anfrage senden'), speichert: t('Wird gesendet …'), gespeichert: t('Gesendet') }}
                title={istMac ? t('Senden (Cmd und Enter)') : t('Senden (Strg und Enter)')}
              />
            </>
          )}
        </footer>
      </div>
    </>
  );
}
